import { Box, Typography, Stack } from "@mui/material";
import { motion } from "framer-motion";
import { PencilRuler, Settings, LifeBuoy, MessageSquare } from "lucide-react";
import { useSEO } from "../lib/seo";
import { Reveal } from "../site/Reveal";
import { CTASection } from "../site/CTASection";
// Motion-enabled MUI Box so we can keep the original framer-motion entrance animation
const MotionBox = motion(Box);

const SERVICES = [
  {
    n: "01",
    icon: PencilRuler,
    t: "Custom Design & Manufacturing",
    d: "Stabilizers, transformers and CVTs engineered to your exact specifications — voltage range, phase, kVA rating and site conditions.",
  },
  {
    n: "02",
    icon: Settings,
    t: "Installation & Commissioning",
    d: "On-site installation and commissioning support to get your equipment running safely and correctly from day one.",
  },
  {
    n: "03",
    icon: LifeBuoy,
    t: "After-Sales Service & Maintenance",
    d: "Preventive maintenance and responsive after-sales support to maximise uptime and extend equipment life.",
  },
  {
    n: "04",
    icon: MessageSquare,
    t: "Application Consultation",
    d: "Expert guidance on selecting the right stabilizer, transformer or CVT for your application and load profile.",
  },
];

// ---- Design-token shorthands, mirrored from the original Tailwind theme ----
// (kept as CSS custom properties so they still resolve to whatever your
// global theme/CSS defines for --obsidian, --safety, --ink, etc.)
const colors = {
  obsidian: "var(--obsidian, #0a0a0a)",
  safety: "var(--safety, #ff6b35)",
  ink: "var(--ink, #111111)",
  mutedForeground: "var(--muted-foreground, #6b7280)",
  white60: "rgba(255,255,255,0.6)",
  inkBorder15: "rgba(17,17,17,0.15)",
};

const fontDisplay = "var(--font-display, inherit)";
const fontMono = "var(--font-mono, monospace)";

export default function Services() {
  useSEO({
    title:
      "Services | Custom Manufacturing, Installation & Maintenance | ITL Technical Services",
    description:
      "ITL Technical Services offers custom design & manufacturing, installation and commissioning, after-sales maintenance and application consultation for voltage stabilizers and transformers.",
  });

  return (
    <>
      {/* ---------- Header ---------- */}
      <Box
        data-testid="services-header"
        className="grain grain-dark"
        sx={{
          position: "relative",
          color: "#fff",
          overflow: "hidden",
          pt: { xs: 16, md: 22 },
          pb: { xs: 8, md: 12 },

          backgroundImage: `
      linear-gradient(
        rgba(10,10,10,0.75),
        rgba(10,10,10,0.82)
      ),
      url("/ServicesBanner.jpg")
    `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Box
          sx={{
            maxWidth: "1280px", // max-w-7xl
            mx: "auto",
            px: { xs: 2, sm: 3, lg: 4 }, // px-4 sm:px-6 lg:px-8
          }}
        >
          <Typography
            component="p"
            className="mono-label"
            sx={{
              fontFamily: fontMono,
              color: colors.safety,
              mb: 4, // mb-8
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              fontSize: "0.75rem",
              textAlign: "left",
            }}
          >
            [ Services ]
          </Typography>

          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            sx={{ maxWidth: "56rem" }} // max-w-4xl
          >
            <Typography
              component="h1"
              sx={{
                fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                fontWeight: 900,
                letterSpacing: "-0.04em", // tracking-tighter
                lineHeight: 0.85,
                fontSize: { xs: "3rem", md: "4.5rem" }, // text-5xl / md:text-7xl
                textAlign: "left",
              }}
            >
              From spec sheet to service call.
            </Typography>
          </MotionBox>

          <Typography
            sx={{
              color: colors.white60,
              mt: 4, // mt-8
              maxWidth: "36rem", // max-w-xl
              fontSize: "1.125rem", // text-lg
              textAlign: "left",
            }}
          >
            End-to-end support across the full lifecycle of your
            power-conditioning equipment.
          </Typography>
        </Box>
      </Box>

      {/* ---------- Services list ---------- */}
      <Box
        sx={{
          maxWidth: "1280px", // max-w-7xl
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 }, // px-4 sm:px-6 lg:px-8
          py: { xs: 8, md: 12 }, // py-16 md:py-24
        }}
      >
        <Stack sx={{ gap: 0 }}>
          {SERVICES.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <Box
                className="group"
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
                  gap: 3, // gap-6
                  py: { xs: 5, md: 7 }, // py-10 md:py-14
                  borderTop: `1px solid ${colors.inkBorder15}`,
                  "&:last-of-type": {
                    borderBottom: `1px solid ${colors.inkBorder15}`,
                  },
                  alignItems: "start",
                }}
              >
                <Typography
                  sx={{
                    gridColumn: { md: "span 1" },
                    fontFamily: fontMono,
                    color: colors.safety,
                    fontSize: "1.125rem", // text-lg
                  }}
                >
                  [ {s.n} ]
                </Typography>

                <Box sx={{ gridColumn: { md: "span 1" } }}>
                  <Box
                    component={s.icon}
                    sx={{
                      width: 32, // w-8
                      height: 32, // h-8
                      color: colors.ink,
                      transition: "color 0.2s",
                      ".group:hover &": {
                        color: colors.safety,
                      },
                    }}
                    strokeWidth={1.75}
                  />
                </Box>

                <Typography
                  component="h2"
                  sx={{
                    gridColumn: { md: "span 5" },
                    fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                    fontWeight: 900,
                    letterSpacing: "-0.04em", // tracking-tighter
                    fontSize: { xs: "1.875rem", md: "2.25rem" }, // text-3xl md:text-4xl
                    color: colors.ink,
                    transition: "color 0.2s",
                    ".group:hover &": {
                      color: colors.safety,
                    },
                  }}
                >
                  {s.t}
                </Typography>

                <Typography
                  sx={{
                    gridColumn: { md: "span 5" },
                    color: colors.mutedForeground,
                    fontSize: "1.125rem", // text-lg
                    lineHeight: 1.625, // leading-relaxed
                    textAlign: "left",
                  }}
                >
                  {s.d}
                </Typography>
              </Box>
            </Reveal>
          ))}
        </Stack>
      </Box>

      <CTASection />
    </>
  );
}
